<template>
  <div>
    <banner-component :title="feature.h1" :lead="lead" :breadcrumbs="breadcrumbs" ruled>
      <a class="btn btn--primary" :href="site.productUrl" rel="noopener">Accéder au gestionnaire</a>
      <router-link class="btn btn--ghost" to="/gestionnaire-citron">Toutes les fonctions</router-link>
    </banner-component>

    <section class="band">
      <div class="container feature-layout">
        <div class="content">
          <figure v-if="feature.image" class="shot">
            <img :src="feature.image.src" :width="feature.image.width" :height="feature.image.height" :alt="feature.image.alt">
          </figure>
          <section v-for="section in feature.sections" :key="section.h2" class="section">
            <h2>{{ section.h2 }}</h2>
            <content-blocks :blocks="section.blocks"/>
          </section>
        </div>

        <aside class="side" aria-label="Dans le même logiciel">
          <h2>Dans le même logiciel</h2>
          <ul>
            <li v-for="sibling in siblings" :key="sibling.slug">
              <router-link :to="sibling.path">{{ sibling.name }}</router-link>
              <span>{{ sibling.summary }}</span>
            </li>
          </ul>
        </aside>
      </div>
    </section>

    <section class="band band--white" aria-labelledby="related-title">
      <div class="container">
        <h2 id="related-title">Pour aller plus loin</h2>
        <article-list :articles="related"/>
      </div>
    </section>

    <section class="band" aria-labelledby="faq-title">
      <div class="container container--narrow">
        <h2 id="faq-title">Questions fréquentes</h2>
        <faq-section :items="feature.faq"/>
      </div>
    </section>

    <call-to-action-component/>
  </div>
</template>

<script>
  import site from "@/content/site";
  import features from "@/content/features";
  import articles from "@/content/articles";
  import pages from "@/content/pages";
  import {inline} from "@/seo/inline";
  import {useSeo} from "@/seo/useSeo";
  import {organization, software, webPage, breadcrumb, faqPage, absoluteUrl} from "@/seo/schema";
  import BannerComponent from "@/components/BannerComponent.vue";
  import ContentBlocks from "@/components/ContentBlocks.vue";
  import ArticleList from "@/components/ArticleList.vue";
  import FaqSection from "@/components/FaqSection.vue";
  import CallToActionComponent from "@/components/CallToActionComponent.vue";

  /**
   * Page détaillée d'une fonction du Gestionnaire Citron. Le contenu vient de src/content/features.js (prop slug, fournie par le routeur).
   */
  export default {
    name: 'FeatureView',
    components: {BannerComponent, ContentBlocks, ArticleList, FaqSection, CallToActionComponent},
    props: {
      slug: {type: String, required: true},
    },
    setup(props) {
      const feature = features.find(entry => entry.slug === props.slug);
      const page = pages.find(entry => entry.path === feature.path);
      const crumbs = [
        {name: 'Accueil', path: '/'},
        {name: 'Gestionnaire Citron', path: '/gestionnaire-citron'},
        {name: page.name, path: feature.path}
      ];
      useSeo({
        path: feature.path,
        image: `/img/og/${feature.slug}.jpg`,
        graph: [
          organization(),
          software(),
          webPage(feature.path, page.title, page.description, {
            about: {'@id': software()['@id']},
            ...(feature.image ? {primaryImageOfPage: {'@type': 'ImageObject', url: absoluteUrl(feature.image.src), caption: feature.image.alt}} : {}),
            breadcrumb: {'@id': `${absoluteUrl(feature.path)}#breadcrumb`}
          }),
          {...breadcrumb(crumbs), '@id': `${absoluteUrl(feature.path)}#breadcrumb`},
          faqPage(feature.faq)
        ]
      });
    },
    data: function () {
      const feature = features.find(entry => entry.slug === this.slug);
      return {
        site,
        feature,
        lead: inline(feature.lead),
        breadcrumbs: [
          {name: 'Accueil', path: '/'},
          {name: 'Gestionnaire Citron', path: '/gestionnaire-citron'},
          {name: feature.name, path: feature.path}
        ],
        siblings: feature.siblings.map(slug => features.find(entry => entry.slug === slug)),
        related: feature.related.map(slug => articles.find(article => article.slug === slug)),
      }
    }
  }
</script>

<style lang="stylus" scoped>
@import '~/src/common/style/constants.styl';

.feature-layout
  display grid
  gap 48px

.content
  min-width 0
  > .shot
    margin 0 0 40px
    img
      width 100%
      height auto
      border 2px solid INK
      border-radius 4px
      background WHITE
  .section + .section
    margin-top 48px
  .section h2
    margin 0 0 18px
    font-size clamp(1.5rem, 3vw, 2rem)

.side
  align-self start
  padding 20px
  background WHITE
  border 1px solid RULE
  border-top 4px solid LEMON
  border-radius 4px
  h2
    margin 0 0 12px
    font-size 1.25rem
  ul
    list-style none
    margin 0
    padding 0
  li
    margin 0
    padding 12px 0
    border-top 1px solid RULE
    span
      display block
      margin-top 2px
      color MUTED
      font-size 0.92rem

@media (min-width: DESKTOP_SIZE)
  .feature-layout
    grid-template-columns minmax(0, 1fr) 300px
    gap 64px
  .side
    position sticky
    top 96px
</style>
